import approvalProcessService from "@services/approvalProcess/approvalProcessService"
import type {
  ApprovalImportMode,
  ApprovalMatrixOutputDto,
  CloneApprovalMapDto,
  CloneApprovalMapResultDto,
  ImportApprovalProcessResultDto,
} from "@services/approvalProcess/approvalProcessDto"
import type { ApprovalProcessFilters } from "@scenes/systemSetting/components/approvalProcess/helpers/approvalProcessState"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import { action, makeAutoObservable, observable, runInAction } from "mobx"

/**
 * Lỗi nghiệp vụ mang KEY dịch thay vì chuỗi đã dịch sẵn. Store không được phụ thuộc `L()` (đó là việc
 * của tầng UI), nhưng cũng không được để UI hiển thị nguyên key cho người dùng — nên key được truyền
 * qua `localizationKey` và scene tự dịch.
 */
export class LocalizedError extends Error {
  constructor(public readonly localizationKey: string) {
    super(localizationKey)
    this.name = "LocalizedError"
  }
}

class ApprovalProcessStore {
  @observable isLoading!: boolean
  @observable detailLoading!: boolean
  @observable tableData!: PagedResultDto<any>
  @observable detailData!: any
  @observable listApprovalProcess: any[] = []
  @observable listApprovalFlow!: any
  @observable matrixData!: ApprovalMatrixOutputDto
  @observable matrixLoading!: boolean
  @observable matrixError!: string | null

  // Import workflow state (R14.3-R14.5, R14.9). The modal (task 12.2) drives these mutations.
  @observable importFile!: File | null
  @observable importReport!: ImportApprovalProcessResultDto | null
  @observable importToken!: string | null
  @observable importMode!: ApprovalImportMode
  @observable importLoading!: boolean

  constructor() {
    makeAutoObservable(this)
    this.isLoading = false
    this.detailLoading = false
    this.tableData = { items: [], totalCount: 0 }
    this.detailData = {}
    this.listApprovalProcess = []
    this.listApprovalFlow = {}
    this.matrixData = { projects: [], cells: [] }
    this.matrixLoading = false
    this.matrixError = null
    this.importFile = null
    this.importReport = null
    this.importToken = null
    this.importMode = "Strict"
    this.importLoading = false
  }

  @action
  async create(body: any) {
    this.isLoading = true
    if (!body.id) {
      const result = await approvalProcessService
        .create(body)
        .finally(() => (this.isLoading = false))
      this.detailData = result
      this.tableData.items.push(result)
    }
  }
  @action
  async update(body: any) {
    const result = await approvalProcessService
      .update(body)
      .finally(() => (this.isLoading = false))
    this.tableData.items = this.tableData.items.map((x) => {
      if (x.id === body.id) x = result
      return x
    })
  }

  @action
  async active(id: number, isActive) {
    await approvalProcessService.active(id, isActive)
  }

  // Bulk activate/deactivate selected workflows in one atomic backend call (R31.4, R31.5). Keeps
  // the legacy per-row `active` action untouched; the scene handles notify/clear-selection/refresh.
  @action
  async activeMultiple(ids: number[], isActive: boolean): Promise<void> {
    await approvalProcessService.activeMultiple(ids, isActive)
  }

  @action
  async get(id: number) {
    this.detailLoading = true
    const result = await approvalProcessService
      .get(id)
      .finally(() => (this.detailLoading = false))
    this.detailData = result
  }

  @action
  async getAll(params: any) {
    this.isLoading = true
    const result = await approvalProcessService
      .getAll(params)
      .finally(() => (this.isLoading = false))
    this.tableData = result
  }

  @action
  async getMatrix(filters: ApprovalProcessFilters) {
    this.matrixLoading = true
    this.matrixError = null

    try {
      const result = await approvalProcessService.getMatrix({
        modules: filters.modules ?? [],
        projectIds: filters.projectIds ?? [],
        // Trạng thái để trống nghĩa là KHÔNG lọc — phải gửi undefined, giống List (getAll). Nếu ép
        // `true` thì cùng một bộ filter lại cho ra 2 tập dữ liệu khác nhau giữa 2 chế độ xem và người
        // dùng không thể xem "tất cả" ở Matrix.
        isActive:
          filters.isActive === undefined
            ? undefined
            : filters.isActive === "true",
      })
      runInAction(() => {
        this.matrixData = result
      })
      return result
    } catch (error: unknown) {
      runInAction(() => {
        this.matrixError =
          error instanceof Error ? error.message : "Unknown error"
      })
      throw error
    } finally {
      runInAction(() => {
        this.matrixLoading = false
      })
    }
  }

  @action
  async cloneMapToProjects(
    body: CloneApprovalMapDto
  ): Promise<CloneApprovalMapResultDto> {
    return approvalProcessService.cloneMapToProjects(body)
  }

  @action
  async assignMapToProjects(
    body: CloneApprovalMapDto
  ): Promise<CloneApprovalMapResultDto> {
    return approvalProcessService.assignMapToProjects(body)
  }

  // Export the workbook using exactly the applied filters (R12.2, R12.3). Delegates all
  // HTTP/download work to the service; returns "empty" on a 204 so the scene can show the
  // localized empty notice without downloading (R12.9).
  @action
  async exportProcesses(
    filters: ApprovalProcessFilters
  ): Promise<"downloaded" | "empty"> {
    return approvalProcessService.exportApprovalProcesses({
      modules: filters.modules,
      isActive: filters.isActive,
      projectIds: filters.projectIds,
      keyword: filters.keyword,
    })
  }

  // Stage the picked file and reset any previous validation result (R14.4). Selecting a new file
  // invalidates the prior token so a stale confirm cannot persist a different workbook.
  @action
  setImportFile(file: File | null) {
    this.importFile = file
    this.importReport = null
    this.importToken = null
  }

  // Record the explicit Strict / partial choice (R14.5) the modal presents to the user.
  @action
  setImportMode(mode: ApprovalImportMode) {
    this.importMode = mode
  }

  // Clear all import state when the modal closes so a re-open starts clean.
  @action
  resetImport() {
    this.importFile = null
    this.importReport = null
    this.importToken = null
    this.importMode = "Strict"
    this.importLoading = false
  }

  // Download the import template (R14.3); delegates all HTTP/download work to the service.
  @action
  async downloadImportTemplate(): Promise<"downloaded" | "empty"> {
    return approvalProcessService.downloadImportTemplate()
  }

  // Validate-before-persist (R14.4): upload the staged file, keep the per-row report and the
  // single-use token. On failure, clear the report/token so no stale confirm is possible.
  @action
  async validateImport(file: File): Promise<ImportApprovalProcessResultDto> {
    this.importLoading = true
    try {
      const report = await approvalProcessService.validateImport(
        file,
        this.importMode
      )
      runInAction(() => {
        this.importFile = file
        this.importReport = report
        this.importToken = report.validationToken
      })
      return report
    } catch (error: unknown) {
      runInAction(() => {
        this.importReport = null
        this.importToken = null
      })
      throw error
    } finally {
      runInAction(() => {
        this.importLoading = false
      })
    }
  }

  // Persist the reviewed workbook (R14.9) using the staged file + token + chosen mode; keeps the
  // created/updated/skipped result on the store for the summary.
  @action
  async persistImport(): Promise<ImportApprovalProcessResultDto> {
    if (!this.importFile || !this.importToken) {
      throw new LocalizedError("APPROVAL_PROCESS_IMPORT_INVALID_SESSION")
    }
    this.importLoading = true
    try {
      const result = await approvalProcessService.persistImport(
        this.importFile,
        this.importToken,
        this.importMode
      )
      runInAction(() => {
        this.importReport = result
      })
      return result
    } finally {
      runInAction(() => {
        this.importLoading = false
      })
    }
  }

  @action
  async getList(params: any) {
    this.detailLoading = true
    const result = await approvalProcessService
      .getList(params)
      .finally(() => (this.detailLoading = false))
    this.listApprovalProcess = result
  }

  @action
  async getListFlow(params: any) {
    this.detailLoading = true
    const result = await approvalProcessService
      .getListFlow(params)
      .finally(() => (this.detailLoading = false))
    this.listApprovalFlow = result
  }

  @action
  async sendRequest(body: any) {
    this.isLoading = true
    const result = await approvalProcessService
      .sendRequest(body)
      .finally(() => (this.isLoading = false))
    this.listApprovalFlow = result
  }
  @action
  async sendMultiRequest(body: any) {
    this.isLoading = true
    const result = await approvalProcessService
      .sendMultiRequest(body)
      .finally(() => (this.isLoading = false))
    return result
  }
}

export default ApprovalProcessStore
